/**
 * Renders Freya's reply HTML without ever handing server text to
 * `innerHTML`.
 *
 * The string is parsed into an inert document (DOMParser never runs scripts
 * or fires handlers) and rebuilt as React elements from a whitelist:
 *
 *   b · i · s · code · br · a[href^="https://"]
 *
 * Any other tag is unwrapped (its text survives, the tag does not). Tags
 * whose content is code rather than prose (script, style…) are dropped with
 * their content. No attribute survives except a link's https href, and links
 * always open in a new tab with `noopener noreferrer`.
 *
 * Deliberately NOT `sanitizeRichHtml` (DOMPurify): that allowlist is shaped
 * for the News editor (img, headings, lists, http links) and still ends in
 * `dangerouslySetInnerHTML`. A chat reply needs far less.
 */
import { createElement, Fragment, type ReactNode } from 'react';

const KEEP = new Set(['b', 'i', 's', 'code']);
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'noscript', 'template', 'iframe', 'object', 'embed', 'svg', 'math', 'head', 'title',
]);

function walk(node: Node, key: string): ReactNode {
  if (node.nodeType === 3 /* TEXT_NODE */) {
    // Freya writes plain line breaks (\n); render them as <br>, never as
    // collapsed whitespace or literal markup.
    const parts = (node.textContent ?? '').split('\n');
    if (parts.length === 1) return parts[0];
    return createElement(
      Fragment,
      { key },
      ...parts.flatMap((p, i) => (i === 0 ? [p] : [createElement('br', { key: `${key}.br${i}` }), p])),
    );
  }
  if (node.nodeType !== 1 /* ELEMENT_NODE */) return null;

  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROP_WITH_CONTENT.has(tag)) return null;
  if (tag === 'br') return createElement('br', { key });

  const children = Array.from(el.childNodes).map((c, i) => walk(c, `${key}.${i}`));

  if (KEEP.has(tag)) return createElement(tag, { key }, ...children);

  if (tag === 'a') {
    const href = (el.getAttribute('href') ?? '').trim();
    if (/^https:\/\/[^\s]/i.test(href)) {
      return createElement(
        'a',
        { key, href, target: '_blank', rel: 'noopener noreferrer' },
        ...children,
      );
    }
  }
  return createElement(Fragment, { key }, ...children);
}

export function renderSafeHtml(html: string | null | undefined): ReactNode[] {
  if (!html) return [];
  if (typeof DOMParser === 'undefined') return [html];
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return Array.from(doc.body.childNodes).map((n, i) => walk(n, String(i)));
}

export function SafeHtml(props: { html: string | null | undefined }) {
  return <>{renderSafeHtml(props.html)}</>;
}
